<template>
    <div>
        <template v-if="loading">
            <loading-spinner/>
        </template>
        <template v-if="!loading">
            <x-layout>
                <x-flex xs12>
                    <h2>Amendment</h2>
                </x-flex>
            </x-layout>

            <x-layout mt-3>
                <x-flex xs12>
                    <data-list v-model="amendmentDataList" class="amendment-data-list" />
                </x-flex>
            </x-layout>

            <x-layout mt-5>
                <x-flex xs12>
                    <h4>Commentary</h4>

                    <div class="text-editor-output" v-html="pendingAmendment.commentary" />
                </x-flex>
            </x-layout>

            <x-layout mt-5>
                <x-flex xs12>
                    <h4>Details</h4>
                    <x-data-table
                        class="no-row-hover mt-3"
                        :headers="reviewAmendmentHeaders"
                        :items="reviewListSummary"
                        hide-actions
                    >
                        <template v-slot:items="props">
                            <td>{{ props.item.text | capitalize }}</td>
                            <td>{{ props.item.oldValue }}</td>
                            <td>{{ props.item.newValue }}</td>
                        </template>
                    </x-data-table>
                    <template v-if="isStandalonePolicy && hasChangesOnEntities">
                        <br><h4>{{ entityReviewList.title }}</h4>
                        <x-data-table
                            class="no-row-hover mt-3 obligor-data-list"
                            :headers="multiObligorGuarantorHeaders"
                            :items="entityReviewList.value"
                            hide-actions
                        >
                            <template v-slot:items="props">
                                <td>{{ props.item.id ? getFormattedId(props.item.id) : '' }}</td>
                                <td>{{ props.item.text | capitalize }}</td>
                                <td>{{ props.item.oldValue }}</td>
                                <td>{{ props.item.newValue }}</td>
                            </template>
                        </x-data-table>
                    </template>
                    <template v-if="hasObligor">
                        <br><h4>{{ obligorReviewList.title }}</h4>
                        <x-data-table
                            class="no-row-hover mt-3 obligor-data-list"
                            :headers="multiObligorGuarantorHeaders"
                            :items="obligorReviewList.value"
                            hide-actions
                        >
                            <template v-slot:items="props">
                                <td>{{ props.item.id ? getFormattedId(props.item.id) : '' }}</td>
                                <td>{{ props.item.text | capitalize }}</td>
                                <td>{{ props.item.oldValue }}</td>
                                <td>{{ props.item.newValue }}</td>
                            </template>
                        </x-data-table>
                        <template v-if="guarantorReviewList.value.length">
                            <br><h4>{{ guarantorReviewList.title }}</h4>
                            <x-data-table
                                class="no-row-hover mt-3 guarantor-data-list"
                                :headers="multiObligorGuarantorHeaders"
                                :items="guarantorReviewList.value"
                                hide-actions
                            >
                                <template v-slot:items="props">
                                    <td>{{ props.item.id ? getFormattedId(props.item.id) : '' }}</td>
                                    <td>{{ props.item.text | capitalize }}</td>
                                    <td>{{ props.item.oldValue }}</td>
                                    <td>{{ props.item.newValue }}</td>
                                </template>
                            </x-data-table>
                        </template>
                    </template>
                </x-flex>
            </x-layout>

            <x-layout v-if="amendmentHasLineChanges" mt-5>
                <x-flex xs12>
                    <h4>Lines</h4>

                    <x-data-table
                        class="no-row-hover mt-3"
                        :headers="lineAmendmentHeaders"
                        :items="lineReviewList"
                        hide-actions
                    >
                        <template v-slot:items="props">
                            <td>{{ props.item.text }}</td>
                            <td>
                                <span v-if="userCanView(props.item.insurerCompanyId)">{{ 'Line type: ' + props.item.oldLineType }}<br></span>
                                <span v-if="userCanView(props.item.insurerCompanyId)">{{ 'Price: ' + props.item.oldLineTypePrice }}<br></span>
                                <span v-if="props.item.oldAmount">{{ 'Amount: ' + props.item.oldAmount }}<br></span>
                                <span v-if="userCanView(props.item.insurerCompanyId) && props.item.oldBrokerage">{{ 'Brokerage: ' + props.item.oldBrokerage }}<br></span>
                                <span v-if="props.item.oldSecurity">{{ 'Security: ' + props.item.oldSecurity }}<br></span>
                                <span v-if="props.item.oldInsurerRole">{{ 'Role: ' + props.item.oldInsurerRole }}</span>
                            </td>
                            <td>
                                <span v-if="userCanView(props.item.insurerCompanyId)">{{ props.item.newLineType ? 'Line type: ' + props.item.newLineType : '' }}<br></span>
                                <span v-if="userCanView(props.item.insurerCompanyId)">{{ !isEmpty(props.item.newLineTypePrice) ? 'Price: ' + props.item.newLineTypePrice : '' }}<br></span>
                                <span v-if="props.item.newAmount">{{ 'Amount: ' + props.item.newAmount }}<br></span>
                                <span v-if="userCanView(props.item.insurerCompanyId) && props.item.newBrokerage">{{ 'Brokerage: ' + props.item.newBrokerage }}<br></span>
                                <span v-if="props.item.newSecurity">{{ 'Security: ' + props.item.newSecurity }}<br></span>
                                <span v-if="props.item.newInsurerRole">{{ 'Role: ' + props.item.newInsurerRole }}</span>
                            </td>
                        </template>
                    </x-data-table>
                </x-flex>
            </x-layout>

            <x-layout v-if="newLineReviewList" mt-5>
                <x-flex xs12>
                    <h4>New lines</h4>

                    <x-data-table
                        class="no-row-hover mt-3"
                        :headers="newLineAmendmentHeaders"
                        :items="newLineReviewList"
                        hide-actions
                    >
                        <template v-slot:items="props">
                            <tr v-if="userCanView(props.item.insurer_company_id)">
                                <td>{{ getInsurerName(props.item.insurer_company_id) }}</td>

                                <td v-if="props.item.brokerage">{{ props.item.brokerage | percent }}</td>
                                <td v-else>-</td>

                                <td v-if="props.item.insurer_role">{{ props.item.insurer_role }}</td>
                                <td v-else>-</td>

                                <td v-if="props.item.offer_type">{{ getOfferTypeName(props.item.offer_type) }}</td>
                                <td v-else>-</td>

                                <td v-if="props.item.offer_type === 'rate'">{{ props.item.price | numberNoTrail }} BPS</td>
                                <td v-else-if="props.item.offer_type === 'flat_fee'">{{ props.item.flat_fee | currency(getCurrencyCode(true)) }}</td>
                                <td v-else-if="['percentage_of_net_margin', 'percentage_of_gross_margin'].includes(props.item.offer_type)">
                                    {{ props.item.price | numberNoTrail }} BPS
                                    <br>({{ props.item.percentage_of_margin | percent }} {{ typeOfMargin(props.item.offer_type) }} of margin)
                                </td>
                                <td v-else>-</td>

                                <td>{{ props.item.line_amount | currency(getCurrencyCode(true)) }} ({{ props.item.line_percent | percent }})</td>
                            </tr>
                            <tr v-else>
                                <td>{{ getInsurerName(props.item.insurer_company_id) }}</td>
                                <td>-</td>
                                <td>-</td>
                                <td>-</td>
                                <td>-</td>
                                <td>{{ props.item.line_amount | currency(getCurrencyCode(true)) }} ({{ props.item.line_percent | percent }})</td>
                            </tr>
                            <tr v-if="props.item.offer_type && props.item.security">
                                <td colspan="6">{{ props.item.security.name }}</td>
                            </tr>
                        </template>
                    </x-data-table>
                </x-flex>
            </x-layout>

            <x-layout v-if="!this.isStandalonePolicy && informationSectionsItem" mt-5>
                <x-flex xs12>
                    <h4>{{ informationSectionsItem.text }}</h4>

                    <additional-information-display
                        v-model="informationSectionsItem.value"
                        :insurer-list="insurerList"
                        :insurer="isInsurer"
                    />
                </x-flex>
            </x-layout>

            <x-layout mx-2 mb-5>
                <x-flex>
                    <document-list
                        v-if="documentableId"
                        :documentable-id="documentableId"
                        documentable-type="endorsement"
                        :can-delete="() => false"
                    />
                </x-flex>
            </x-layout>

            <div class="mt-5">
                <slot/>
            </div>
        </template>
    </div>
</template>

<script>
    import AdditionalInformationDisplay from '~/components/Shared/UIComponents/AdditionalInformationDisplay';
    import DataList from '~/components/Shared/UIComponents/DataList';
    import productConfig from '~/mixins/productConfig';
    import riskReviewList from '~/mixins/riskReviewList';
    import DocumentList from '~/components/Shared/UIComponents/DocumentList';
    export default {
        name: 'AmendmentReviewList',
        components: {
            AdditionalInformationDisplay,
            DataList,
            DocumentList
        },
        mixins: [
            productConfig,
            riskReviewList,
        ],
        props: {
            declaration: {
                type: Object,
                default: []
            },
        },
        data() {
            return {
                currencies: [],
                sectors: [],
                countries: [],
                obligors: [],
                guarantors: [],
                productsData:[],
                entities: [],
                riskCodes: [],
                loading: true,
                reviewAmendmentHeaders: [
                    {
                        text: 'Field',
                        value: 'text',
                        sortable: false
                    },
                    {
                        text: 'Old value',
                        value: 'oldValue',
                        sortable: false
                    },
                    {
                        text: 'Amendment value',
                        value: 'newValue',
                        sortable: false
                    }
                ],
                lineAmendmentHeaders: [
                    {
                        text: 'Insurer',
                        value: 'text',
                        sortable: false
                    },
                    {
                        text: 'Old written line',
                        value: 'oldValue',
                        sortable: false
                    },
                    {
                        text: 'New written line',
                        value: 'newValue',
                        sortable: false
                    }
                ],
                newLineAmendmentHeaders: [
                    {
                        text: 'Insurer',
                        value: 'insurer',
                        sortable: false
                    },
                    {
                        text: 'Brokerage',
                        value: 'brokerage',
                        sortable: false
                    },
                    {
                        text: 'Role',
                        value: 'insurer_role',
                        sortable: false
                    },
                    {
                        text: 'Type',
                        value: 'type',
                        sortable: false
                    },
                    {
                        text: 'Price',
                        value: 'price',
                        sortable: false
                    },
                    {
                        text: 'Written line',
                        value: 'line_percent',
                        sortable: false
                    }
                ],
                multiObligorGuarantorHeaders: [],
                facilityInsurers: []
            }
        },
        computed: {
            reviewLists() {
                return this.reviewList;
            },
            product() {
                return this.declaration.product;
            },
            endorsements() {
                return this.declaration.endorsements.filter(e => e.type === 'amendment' || e.type === 'off_risk');
            },
            pendingAmendment() {
                return this.endorsements.find(endorsement => endorsement.type === 'amendment' && this.isPendingAmendment(endorsement));
            },
            isStandalonePolicy() {
                return this.declaration.risk_structure === 'standalone_policy';
            },
            riskUnderReview() {
                return this.declaration;
            },
            comparisonData() {
                return this.pendingAmendment.field_changes;
            },
            amendmentEffectiveDate() {
                return this.pendingAmendment.effective_date;
            },
            amendmentDataList() {
                return [
                    {
                        text: 'Effective date',
                        value: this.$options.filters.formatDate(this.amendmentEffectiveDate, undefined, 'YYYY-MM-DD')
                    }
                ];
            },
            idle() {
                return this.status === 'idle';
            },
            amendmentHasLineChanges() {
                let hasLineChanges = false;

                if (this.pendingAmendment.field_changes.hasOwnProperty('lines')) {
                    const lineChanges = this.pendingAmendment.field_changes.lines;

                    this.declaration.lines.forEach(function (oldLine) {
                        const newLine = lineChanges.find(({original_line_id = null}) => original_line_id === oldLine.id);

                        if (newLine) {
                            const lineTypeOrPriceUpdated = ['price', 'flat_fee', 'percentage_of_net_margin', 'percentage_of_gross_margin'].filter(value => { return Object.keys(newLine).includes(value) }).length > 0;
                            if ((Boolean(newLine.line_percent) && newLine.line_percent !== oldLine.line_percent) ||
                                (Boolean(newLine.line_amount) && newLine.line_amount !== oldLine.line_amount) ||
                                (Boolean(newLine.brokerage) && newLine.brokerage !== oldLine.brokerage) ||
                                (Boolean(newLine.security_id) && newLine.security_id !== oldLine.security_id) ||
                                (Boolean(newLine.offer_type) && newLine.offer_type !== oldLine.offer_type) ||
                                (Object.prototype.hasOwnProperty.call(newLine, 'insurer_role') && (newLine.insurer_role || null) !== (oldLine.insurer_role || null)) ||
                                lineTypeOrPriceUpdated
                            ) {
                                hasLineChanges = true;
                            }
                        }
                    });
                }

                return hasLineChanges;
            },
            lineReviewList() {
                if (this.amendmentHasLineChanges) {
                    const self = this;
                    const lineReviewList = [];
                    const lineChanges = this.pendingAmendment.field_changes.lines;

                    this.declaration.lines.forEach(function (oldLine) {
                        if (oldLine.captive_insurer_company_id) {
                            oldLine.insurer_company = oldLine.captive_insurer_company;
                        }
                        const lineChange = {
                            insurerCompanyId: oldLine.insurer_company.id,
                            text: oldLine.insurer_company.name,
                            oldAmount: self.applyFormatFilter(oldLine.line_amount, 'currency') + ' (' + self.applyFormatFilter(oldLine.line_percent, 'percent') + ')',
                            newAmount: '',
                            oldBrokerage: self.applyFormatFilter(oldLine.brokerage, 'percent'),
                            newBrokerage: '',
                            oldSecurity: oldLine.security ? oldLine.security.name : '',
                            newSecurity: '',
                            oldInsurerRole: oldLine.insurer_role || '',
                            newInsurerRole: '',
                            oldLineType: self.getOfferTypeName(oldLine.offer_type),
                            newLineType: '',
                            oldLineTypePrice: self.formatLineTypePrice(oldLine.offer_type, oldLine, oldLine),
                            newLineTypePrice: '',
                        };
                        const newLine = lineChanges.find(({original_line_id = null}) => original_line_id === oldLine.id);

                        if (newLine) {
                            if (self.amendmentHasLineChanges) {
                                const newLineAmount = !self.isEmpty(newLine.line_amount) ? newLine.line_amount : oldLine.line_amount;
                                const newLinePercent = !self.isEmpty(newLine.line_percent) ? newLine.line_percent : oldLine.line_percent;
                                const newLineBrokerage = !self.isEmpty(newLine.brokerage) ? newLine.brokerage : oldLine.brokerage;
                                const newFormattedLineType = newLine.offer_type ? self.getOfferTypeName(newLine.offer_type) : lineChange.oldLineType;
                                const newLineType = newLine.offer_type || oldLine.offer_type;
                                const newLineTypePrice = self.formatLineTypePrice(newLineType, newLine, oldLine);

                                let updatedSecurityName = null;
                                if (newLine.security_id !== oldLine.security_id) {
                                    const updatedSecurity = self.getFacilitySecurity(newLine.security_id);

                                    updatedSecurityName =  updatedSecurity ? updatedSecurity.name : null;
                                }

                                const newLineSecurity = updatedSecurityName || lineChange.oldSecurity;

                                lineChange.newAmount = self.applyFormatFilter(newLineAmount, 'currency') + ' (' + self.applyFormatFilter(newLinePercent, 'percent') + ')';
                                lineChange.newBrokerage = self.applyFormatFilter(newLineBrokerage, 'percent');
                                lineChange.newSecurity = newLineSecurity;
                                lineChange.newLineType = newFormattedLineType;
                                lineChange.newLineTypePrice = newLineTypePrice;

                                if (Object.prototype.hasOwnProperty.call(newLine, 'insurer_role')) {
                                    if (newLine.insurer_role) {
                                        lineChange.newInsurerRole = newLine.insurer_role;
                                    } else if (oldLine.insurer_role) {
                                        lineChange.newInsurerRole = '';
                                    }
                                }
                            }
                        }

                        lineReviewList.push(lineChange);
                    });

                    return lineReviewList;
                }
            },
            newLineReviewList() {
                if (this.pendingAmendment.field_changes.hasOwnProperty('new_lines') && this.pendingAmendment.field_changes.new_lines.length > 0) {
                    let newLines = this.pendingAmendment.field_changes.new_lines;

                    const self = this;
                    newLines.forEach(function (newLine) {
                        if (newLine.security_id) {
                            newLine.security = self.getFacilitySecurity(newLine.security_id);
                        }
                    });

                    return newLines;
                }
            },
            hasCaptiveInsurerOffer() {
                if (!this.declaration.id) {
                    return false;
                }

                return Boolean(this.declaration.lines.find(line => Boolean(line.captive_insurer_company_id)));
            },
            insurerList() {
                let insurerList = this.declaration.lines.map(line => {
                        if (line.firm_order_offer.captive_insurer_company_id) {
                            return line.firm_order_offer.captive_insurer_company;
                        }
                        return line.firm_order_offer.insurer_company;
                    });

                if (this.newLineReviewList) {
                    this.newLineReviewList.forEach(newLine => {
                        const matchedInsurer = this.facilityInsurers.find(insurer => insurer.id === newLine.insurer_company_id);

                        if (matchedInsurer) {
                            insurerList.push(matchedInsurer);
                        }
                    });
                }

                if (this.pendingAmendment.hasOwnProperty('endorsement_requests')) {
                    this.pendingAmendment.endorsement_requests.forEach(endorsementRequest => {
                        if (!insurerList.find(insurer => insurer?.id === endorsementRequest.insurer_company_id)) {
                            insurerList.push(endorsementRequest.insurer_company);
                        }
                    });
                }

                return insurerList.filter(insurer => typeof insurer === 'object' && insurer !== null);
            },
            isInsurer() {
                return this.$root.user.company &&
                         (this.$root.user.company.company_type === 'insurer' || this.$root.user.company.company_type === 'captive_insurer');
            },
            hasNoObligor() {
                return (typeof (this.productsData.find(p => p.id === this.declaration.product_id && p.has_no_obligor)) !== 'undefined');
            },
            hasObligor() {
                return !this.hasNoObligor;
            },
            hasChangesOnEntities() {
                return this.entityReviewList.value.length < 1;
            },
            documentableId() {
                if (this.pendingAmendment) {
                    return this.pendingAmendment.id;
                }
                return this.declaration.endorsement_id;
            },
        },
        mounted () {
            this.multiObligorGuarantorHeaders = [
                ...[
                    {
                        text: '',
                        value: 'text',
                        sortable: false
                    }
                ],
                ...this.reviewAmendmentHeaders
            ];
            this.fetchData();
        },
        methods: {
            getCurrencyCode(useNewCode = false) {
                let {currency} = this.declaration;

                if (!useNewCode) {
                    return currency.code;
                }

                if (this.pendingAmendment.field_changes.currency_id) {
                    currency = this.currencies.find(currency => this.pendingAmendment.field_changes.currency_id === currency.id) || {};
                }

                return currency.code;
            },
            getInsurerName(id) {
                const matchedInsurer = this.insurerList.find(insurer => insurer?.id === id);
                return matchedInsurer ? matchedInsurer.name : '';
            },
            getCountryName(id = null) {
                if (id) {
                    return this.countries.find(country => country.id === id).name
                }
                return '';
            },
            getOfferTypeName(type) {
                switch (type) {
                    case 'rate':
                        return 'Rate';
                    case 'flat_fee':
                        return 'Flat fee';
                    case 'percentage_of_net_margin':
                        return 'Percentage of net margin';
                    case 'percentage_of_gross_margin':
                        return 'Percentage of gross margin';
                    case 'retained_percent':
                        return 'Retained percent';
                }
            },
            formatLineTypePrice(type, line, fallbackLine) {
                if (this.isTypeRate(type) || this.isTypeRetainedPercent(type)) {
                    return !this.isEmpty(line.price) ? line.price : fallbackLine.price;
                }

                if (this.isTypeFlatFee(type)) {
                    return !this.isEmpty(line.flat_fee) ? line.flat_fee : fallbackLine.flat_fee;
                }

                if (this.isTypePercentageOfMargin(type)) {
                    return (!this.isEmpty(line.price) ? line.price : fallbackLine.price) + ' (' + (line.percentage_of_margin || fallbackLine.percentage_of_margin) + '%)'
                }
            },
            isTypeRate(type) {
                return type === 'rate';
            },
            isTypeFlatFee(type) {
                return type === 'flat_fee';
            },
            isTypePercentageOfMargin(type) {
                return type === 'percentage_of_net_margin' || type === 'percentage_of_gross_margin';
            },
            isTypeRetainedPercent(type) {
                return type === 'retained_percent';
            },
            userCanView(companyId) {
                if (!this.isInsurer) {
                    return true;
                }

                return this.$root.user.company.id === companyId;
            },
            async fetchData() {
                const dataRequests = [];

                dataRequests.push(axios.get(this.$root.api(`currency`)).then(res => {
                    this.currencies = res.data.data;
                }));
                dataRequests.push(axios.get(this.$root.api(`sectors`)).then(res => {
                    this.sectors = res.data.data;
                }));
                dataRequests.push(axios.get(this.$root.api(`country`)).then(res => {
                    this.countries = res.data.data;
                }));
                dataRequests.push(axios.get(this.$root.api(`product`)).then(res => {
                    this.productsData = res.data.data;
                }));
                dataRequests.push(axios.get(this.$root.api(`entity`)).then(res => {
                    this.entities = res.data.data;
                }));

                dataRequests.push(axios.get(this.$root.api('risk-codes')).then(res => {
                    this.riskCodes = res.data.data;
                }));

                if (this.declaration.facility) {
                    this.facilityInsurers = this.declaration.facility.insurer_companies;
                } else {
                    dataRequests.push(axios.get(this.$root.api(`insurer`)).then(res => {
                        this.facilityInsurers = res.data.data;
                    }));
                }

                axios.all(dataRequests).then(() => {
                    if (this.hasObligor || this.isStandalonePolicy) {
                        axios.get(this.$root.api(`obligor`)).then(res => {
                            this.obligors = res.data.data;
                            this.loading = false;
                        });
                    } else {
                        this.loading = false;
                    }
                });
            },
            applyFormatFilter(value, format, addValue = '') {
                switch (format) {
                    case 'currency':
                        return this.$options.filters.currency(value, addValue);
                    case 'days':
                    case 'risk_period':
                        return this.$options.filters.yearsAndDays(value);
                    case 'percent':
                        return this.$options.filters.percent(value);
                    case 'yes_no':
                    case 'boolean':
                        return value ? 'Yes' : 'No';
                    case 'decimal':
                        return value ? Number.parseFloat(value).toFixed(addValue ? addValue : 2) : ''
                    default:
                        return value || '';
                }
            },
            getFormattedId(id) {
                return id + '.';
            },
            getFacilitySecurity(securityId) {
                return this.declaration.facility?.securities.find(security => security.id === securityId) || {};
            },
            getEntityName(id) {
                return this.entities.find((enitity) => id === enitity.id)?.name || ''
            },
            isEmpty(value) {
                return value === null || typeof value === 'undefined' || value === '';
            },
            isPendingAmendment(amendment) {
                return ['pending', 'in_progress'].includes(amendment.status);
            },
            typeOfMargin(type) {
                if (type === 'percentage_of_net_margin') {
                    return 'net';
                } else if (type === 'percentage_of_gross_margin') {
                    return 'gross';
                }
            },
        }
    }
</script>

<style lang="scss" scoped>
    :deep() {
        table.amendment-data-list {
            tbody td {
                height: 48px;
                padding: 0 12px;
                vertical-align: middle;
            }
        }

        table.x-datatable {
            tbody td:first-child {
                color: $color-grey-4;
            }
        }

        .obligor-data-list, .guarantor-data-list{
            .x-datatable tbody tr td:first-child {
                width: 1%;
            }
        }
    }
</style>
